import type * as React from 'react';
import type { MenuItem, UiAction, UiViewDefinition } from '@tyndo/types';
import { menuRegistry } from './MenuRegistry';
import { actionRegistry } from './ActionRegistry';
import { viewRegistry } from './ViewRegistry';
import { moduleManifestRegistry } from './ModuleManifestRegistry';

/**
 * Register the shell-owned "My Approvals" surface (UI-3) as a synthetic module.
 *
 * "My Approvals" is not a federated module — it lives in the shell and
 * aggregates the per-module approval providers registered with the
 * ApprovalProviderRegistry. To give it a nav entry AND a resolvable route with
 * zero bespoke routing, we register it into the same menu / view / action
 * registries a real module manifest flows through. ModuleView then resolves
 * `/tyndo.approvals/my-approvals` exactly like any module view.
 *
 * The view loader returns the pre-imported shell component directly (no
 * federation), so the surface is available the moment the shell boots — it does
 * not depend on any module having loaded yet (providers light up progressively
 * as modules register them; the view subscribes to the ApprovalProviderRegistry
 * revision and re-renders).
 *
 * Idempotent: safe to call on every manifest (re)load. The registries dedupe by
 * key, and the manifest bootstrap clears them first anyway, so this simply
 * re-adds the synthetic entries after each clear.
 */

export const APPROVALS_MODULE_ID = 'tyndo.approvals';
export const APPROVALS_MODULE_NAME = 'My Approvals';
export const APPROVALS_VIEW_ID = 'tyndo.approvals.view.my_approvals';
/** Route slug ModuleView resolves to APPROVALS_VIEW_ID: `/tyndo.approvals/my_approvals`. */
export const APPROVALS_VIEW_SLUG = 'my_approvals';

export function registerApprovalsShellModule(
  component: React.ComponentType<Record<string, unknown>>,
): void {
  // Module-level identity so HomePage / ⌘K / breadcrumbs can name the surface.
  moduleManifestRegistry.register([
    {
      moduleId: APPROVALS_MODULE_ID,
      name: APPROVALS_MODULE_NAME,
      icon: 'CheckSquare',
    },
  ]);

  // Nav entry — a single top-level item (sequence 5 keeps it near the top of
  // the sidebar, ahead of the module groups, since it's a cross-cutting inbox).
  const menuItem: MenuItem = {
    menuId: 'tyndo.approvals.root',
    label: APPROVALS_MODULE_NAME,
    icon: 'CheckSquare',
    actionId: 'tyndo.approvals.action.open',
    sequence: 5,
  };
  menuRegistry.register(
    APPROVALS_MODULE_ID,
    [menuItem as unknown as Parameters<typeof menuRegistry.register>[1][number]],
    'replace',
  );

  // Action the nav entry dispatches — opens the aggregated view.
  const openAction: UiAction = {
    actionId: 'tyndo.approvals.action.open',
    label: 'Open My Approvals',
    actionType: 'window',
    viewType: 'view',
    viewId: APPROVALS_VIEW_ID,
    model: APPROVALS_MODULE_ID,
  };
  actionRegistry.register(APPROVALS_MODULE_ID, [openAction]);

  // The view itself — a local loader returns the shell component directly.
  const viewDef: UiViewDefinition = {
    viewId: APPROVALS_VIEW_ID,
    viewName: APPROVALS_MODULE_NAME,
    viewType: 'view' as UiViewDefinition['viewType'],
    entityType: 'approval',
    configuration: {},
    actions: [],
    isDefault: true,
    model: APPROVALS_MODULE_ID,
    moduleId: APPROVALS_MODULE_ID,
    moduleName: APPROVALS_MODULE_NAME,
  };
  viewRegistry.register(viewDef, () =>
    Promise.resolve({ default: component }),
  );
}
